import { useEffect, useRef } from "react";
import { translateStaticText, useI18n } from "@/lib/i18n";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "SVG", "PATH"]);
const ATTRS = ["placeholder", "title", "aria-label"] as const;

export function AutoTranslator() {
  const { lang } = useI18n();
  const textOriginalsRef = useRef<WeakMap<Text, string>>(new WeakMap());
  const translatedTextNodesRef = useRef<Set<Text>>(new Set());
  const attrOriginalsRef = useRef<WeakMap<Element, Map<string, string>>>(new WeakMap());
  const translatedAttrElsRef = useRef<Set<Element>>(new Set());

  useEffect(() => {
    if (typeof window === "undefined") return;

    const restore = () => {
      for (const node of translatedTextNodesRef.current) {
        const original = textOriginalsRef.current.get(node);
        if (original != null && node.isConnected) node.nodeValue = original;
      }
      translatedTextNodesRef.current.clear();
      for (const el of translatedAttrElsRef.current) {
        const map = attrOriginalsRef.current.get(el);
        if (!map || !el.isConnected) continue;
        for (const [attr, value] of map) el.setAttribute(attr, value);
      }
      translatedAttrElsRef.current.clear();
    };

    if (lang !== "ar") {
      restore();
      return;
    }

    const applyToText = (node: Text) => {
      const parent = node.parentElement;
      if (!parent || SKIP_TAGS.has(parent.tagName) || parent.closest("[data-no-translate]")) return;
      const current = node.nodeValue ?? "";
      const storedOriginal = textOriginalsRef.current.get(node);
      let original = storedOriginal ?? current;
      if (storedOriginal && current.trim() !== translateStaticText(storedOriginal.trim(), "ar")) original = current;
      const translated = translateStaticText(original.trim(), "ar");
      if (translated === original.trim()) return;
      const lead = original.match(/^\s*/)?.[0] ?? "";
      const tail = original.match(/\s*$/)?.[0] ?? "";
      const next = lead + translated + tail;
      if (current === next) return;
      textOriginalsRef.current.set(node, original);
      translatedTextNodesRef.current.add(node);
      node.nodeValue = next;
    };

    const applyToAttrs = (el: Element) => {
      if (SKIP_TAGS.has(el.tagName) || el.closest("[data-no-translate]")) return;
      for (const attr of ATTRS) {
        const current = el.getAttribute(attr);
        if (!current) continue;
        const map = attrOriginalsRef.current.get(el);
        const storedOriginal = map?.get(attr);
        let original = storedOriginal ?? current;
        if (storedOriginal && current.trim() !== translateStaticText(storedOriginal.trim(), "ar")) original = current;
        const translated = translateStaticText(original.trim(), "ar");
        if (translated === original.trim()) continue;
        if (current === translated) continue;
        if (!map) attrOriginalsRef.current.set(el, new Map([[attr, original]]));
        else if (!map.has(attr)) map.set(attr, original);
        translatedAttrElsRef.current.add(el);
        el.setAttribute(attr, translated);
      }
    };

    const walk = (root: Node) => {
      const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
      let n: Node | null = tw.currentNode;
      while (n) {
        if (n.nodeType === Node.TEXT_NODE) applyToText(n as Text);
        else if (n.nodeType === Node.ELEMENT_NODE) applyToAttrs(n as Element);
        n = tw.nextNode();
      }
    };

    walk(document.body);

    const queued: MutationRecord[] = [];
    let timer: number | null = null;
    const observer = new MutationObserver((mutations) => {
      queued.push(...mutations);
      if (timer != null) return;
      timer = window.setTimeout(() => {
        timer = null;
        const batch = queued.splice(0, queued.length);
        for (const m of batch) {
          if (m.type === "childList") {
            m.addedNodes.forEach((n) => {
              if (n.nodeType === Node.TEXT_NODE) applyToText(n as Text);
              else if (n.nodeType === Node.ELEMENT_NODE) walk(n);
            });
          } else if (m.type === "characterData" && m.target.nodeType === Node.TEXT_NODE) {
            applyToText(m.target as Text);
          } else if (m.type === "attributes" && m.target.nodeType === Node.ELEMENT_NODE) {
            applyToAttrs(m.target as Element);
          }
        }
      }, 0);
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTRS] });

    return () => {
      observer.disconnect();
      if (timer != null) clearTimeout(timer);
    };
  }, [lang]);

  return null;
}